import React, { useCallback, useEffect, useState } from 'react';
import { AudienceAutocomplete, jBusinessContactList, jBusinessContactLittle, JUserApi } from 'jamespot-user-api';
import { useAbortController } from '../../hooks/useAbortController';
import { useIntl } from 'react-intl';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCBusinessContactList, JRCBusinessContactListProps } from '../JRCBusinessContactList/JRCBusinessContactList';

export type JRCBusinessContactsProps = {
    handlers: { user: Pick<JUserApi, 'userBusinessContactQueryList'> };
    onError?: (message?: string) => void;
    initialBusinessContacts: AudienceAutocomplete[];
    onSelectBusinessContact?: (users: AudienceAutocomplete[]) => void;
};

const LIMIT_PER_PAGE = 8;

const businessContactToAutocomplete = (
    businessContact: Required<JRCBusinessContactListProps>['list']['data'][number],
): AudienceAutocomplete => ({
    title: businessContact.title,
    Pseudo: businessContact.title,
    _url: businessContact._url,
    id: String(businessContact.id),
    uri: businessContact.uri,
    shortUri: businessContact.uri,
    name: businessContact.title,
    mainType: 'article',
    type: 'businessContactCard',
    label: businessContact.title,
    cssClass: '',
    cssColor: '',
    class: '',
});

export const JRCBusinessContacts = ({
    handlers,
    onError,
    initialBusinessContacts,
    onSelectBusinessContact,
}: JRCBusinessContactsProps) => {
    const [isLoading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [businessContacts, setBusinessContacts] = useState<JRCBusinessContactListProps['list']>();
    const [selectedBusinessContacts, setSelectedBusinessContacts] =
        useState<AudienceAutocomplete[]>(initialBusinessContacts);
    const [query, setQuery] = useState<string>();

    const intl = useIntl();
    const abortController = useAbortController();

    const selectBusinessContact = useCallback(
        (businessContact: jBusinessContactLittle) => {
            setSelectedBusinessContacts((selected) => {
                const newSelected = selected.some((u) => u.uri === businessContact.uri)
                    ? selected.filter((u) => u.uri !== businessContact.uri)
                    : [...selected, businessContactToAutocomplete(businessContact)];
                if (onSelectBusinessContact) {
                    onSelectBusinessContact(newSelected);
                }
                return newSelected;
            });
        },
        [onSelectBusinessContact],
    );

    useEffect(() => {
        setLoading(true);
        handlers.user
            .userBusinessContactQueryList<jBusinessContactList>(
                {
                    page,
                    limit: LIMIT_PER_PAGE,
                    format: 'raw-list',
                    query,
                },
                { signal: abortController.signal },
            )
            .then((res) => {
                setBusinessContacts({
                    limit: res.result.limit ?? undefined,
                    page: page,
                    data: res.result.data,
                    cnt: res.result.cnt,
                });
                setLoading(false);
            })
            .catch((e) => {
                if (onError && !abortController.signal.aborted) {
                    onError(e.errorMsg || intl.formatMessage({ id: 'GLOBAL_Error' }));
                }
            });
    }, [abortController.signal, handlers.user, intl, onError, page, query]);

    return (
        <JRCBusinessContactList
            isLoading={isLoading}
            onPageChange={setPage}
            list={businessContacts}
            onClick={(businessContact) => selectBusinessContact(businessContact)}
            actionComponent={(businessContact) => (
                <JRCButton
                    label={intl.formatMessage({ id: 'GLOBAL_Select' })}
                    onClick={() => selectBusinessContact(businessContact)}
                    variant={
                        selectedBusinessContacts.some((sbc) => sbc.uri === businessContact.uri)
                            ? 'contained'
                            : 'outlined'
                    }
                />
            )}
            onSearch={setQuery}
        />
    );
};
